import { checkOrganizationNameDuplicateApi, createOrganizationApi } from "@/api/services/admin/organization";
import { TDuplicatedOrganization } from "@/api/services/admin/organization/dtos/check-organization-name.dto";
import { TCreateOrganizationReqDto } from "@/api/services/admin/organization/dtos/create-organization.dto";
import { IconChevronRight } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DuplicateOrganizationNameModal } from "@/features/organization/components/DuplicateOrganizationNameModal";
import { OrganizationCreateBasicInfo } from "@/features/organization/components/organization-create/OrganizationCreateBasicInfo";
import { OrganizationCreateOtherInfo } from "@/features/organization/components/organization-create/OrganizationCreateOtherInfo";
import { OrganizationCreateProductSelect } from "@/features/organization/components/organization-create/OrganizationCreateProductSelect";
import { createOrganizationSchema } from "@/features/organization/schema/createOrganizationSchema";
import { useToast } from "@/hooks/useToast";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

type TSubmitMode = "save" | "saveAndSetup";

const OrganizationCreatePage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const skipBlockerRef = useRef(false);
  const pendingModeRef = useRef<TSubmitMode>("save");
  const duplicateNameModal = useVisible();
  const [duplicates, setDuplicates] = useState<TDuplicatedOrganization[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<TCreateOrganizationReqDto>({
    defaultValues: {
      name: "",
      type: "",
      province: "",
      municipality: "",
      crmLink: "",
      adminInfo: "",
      adminNotes: "",
      productIds: [],
    },
    resolver: yupResolver(createOrganizationSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, setValue, getValues } = methods;
  const { errors } = methods.formState;

  const nameValue = useWatch({ control, name: "name" });
  const selectedProductIds = useWatch({ control, name: "productIds" }) ?? [];

  const runCreate = async (mode: TSubmitMode) => {
    setIsSubmitting(true);
    try {
      const data = methods.getValues();
      if (mode === "saveAndSetup") {
        const res = await createOrganizationApi(data);
        toast.success(API_SUCCESS_MESSAGE);
        skipBlockerRef.current = true;
        navigate(AppRoutes.organizationCreateSetup.replace(":id", String(res.data.id)), { state: { fromCreate: true } });
        return;
      }
      await createOrganizationApi(data);
      toast.success(API_SUCCESS_MESSAGE);
      skipBlockerRef.current = true;
      navigate(AppRoutes.organizations);
    } catch (error) {
      setErrorForm(methods, error);
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkNameAndRun = async (mode: TSubmitMode) => {
    const enteredName = methods.getValues("name")?.trim() ?? "";
    if (!enteredName) return;
    try {
      const result = await checkOrganizationNameDuplicateApi(enteredName);
      if (result.isDuplicated && result.duplicates.length > 0) {
        pendingModeRef.current = mode;
        setDuplicates(result.duplicates);
        duplicateNameModal.open();
        return;
      }
      await runCreate(mode);
    } catch (error) {
      showError(error);
    }
  };

  const handleConfirmDuplicate = async () => {
    duplicateNameModal.close();
    await runCreate(pendingModeRef.current);
  };

  const onSubmit = async (_data: TCreateOrganizationReqDto) => {
    await checkNameAndRun("save");
  };

  const onSubmitAndSetup = async () => {
    const isValid = await methods.trigger();
    const hasProduct = (getValues("productIds") ?? []).length > 0;
    if (!hasProduct) {
      methods.setError("productIds", {
        type: "manual",
        message: "「契約対象」を選択してください。",
      });
    } else {
      methods.clearErrors("productIds");
    }
    if (!isValid || !hasProduct) return;
    await checkNameAndRun("saveAndSetup");
  };

  const handleBack = () => {
    navigate(AppRoutes.organizations);
  };

  const handleToggleProduct = useCallback((productId: number) => {
    const currentIds = getValues("productIds") ?? [];
    const newIds = currentIds.includes(productId) ? currentIds.filter((id) => id !== productId) : [...currentIds, productId];
    setValue("productIds", newIds, { shouldDirty: true, shouldValidate: true });
    if (newIds.length > 0) {
      methods.clearErrors("productIds");
    }
  }, []);

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "基本情報",
        content: <OrganizationCreateBasicInfo control={control} getValues={getValues} setValue={setValue} />,
      },
      {
        title: "その他情報",
        content: <OrganizationCreateOtherInfo control={control} />,
      },
      {
        title: "契約対象",
        content: (
          <OrganizationCreateProductSelect
            selectedProductIds={selectedProductIds}
            onToggleProduct={handleToggleProduct}
            error={errors.productIds?.message}
          />
        ),
      },
    ],
    [control, getValues, selectedProductIds, handleToggleProduct, errors.productIds?.message],
  );

  return (
    <Form
      height="100%"
      methods={methods}
      onSubmit={onSubmit}
      schema={createOrganizationSchema}
      shouldBlock={({ currentLocation, nextLocation }) => {
        if (skipBlockerRef.current) return false;
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return true;
      }}
    >
      <Stack gap={3}>
        <Stack gap={2}>
          <Typography variant="head20Bold" color={text.primary}>
            新規組織の登録 1/2
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            新しい組織の基本情報および契約情報を入力してシステムに登録します。
          </Typography>
        </Stack>

        <MenuNavigate
          menuPrefix={
            <Typography
              variant="body14Bold"
              color={text.primary}
              px={2}
              pb={1}
              sx={{
                wordBreak: "break-word",
              }}
            >
              {nameValue ? `(${nameValue})` : "(登録支援機関名未入力)"}
            </Typography>
          }
          contents={contents}
          contentAffix={
            <StickyFooter>
              <Stack width="100%" justifyContent="space-between" flexDirection="row">
                <Button size="sm" variant="ghost" onClick={handleBack}>
                  戻る
                </Button>
                <Stack direction="row" gap={2}>
                  <Button size="sm" type="submit" variant="outline">
                    <Typography variant="body14Semi">保存だけして戻る</Typography>
                  </Button>
                  <Button
                    size="sm"
                    onClick={onSubmitAndSetup}
                    sx={{
                      paddingX: "16px !important",
                    }}
                  >
                    <IconChevronRight
                      color="currentColor"
                      style={{
                        height: 20,
                        width: 20,
                      }}
                    />
                    <Typography variant="body14Semi" pl={1}>
                      保存して初期設定を開始
                    </Typography>
                  </Button>
                </Stack>
              </Stack>
            </StickyFooter>
          }
        />
      </Stack>

      <DuplicateOrganizationNameModal
        open={duplicateNameModal.visible}
        duplicates={duplicates}
        isLoading={isSubmitting}
        onClose={duplicateNameModal.close}
        onConfirm={handleConfirmDuplicate}
      />
    </Form>
  );
};

export default OrganizationCreatePage;
